import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ManagementState } from 'store/modules/management_store/types';
import moment from 'moment';
import { getDocumentTypesList } from 'services/actions/documents_count';
import { saveDocumentToDatabase } from 'store/modules/documents_store/actions';
import { DocumentsGroup, DocumentsState } from 'store/modules/documents_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import { Container } from './styles';
import FileDocumentData from '../file_document_data';

interface ModalAddDocumentProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  documentGroupList: Array<DocumentsGroup>;
}
interface DocumentsTypes {
  [key: string]: string;
}

interface FilesDescriptionTypes {
  [key: string]: string;
}

interface UploadProgressTypes {
  [key: string]: number;
}

const ModalAddDocument = (props: ModalAddDocumentProps) => {
  const dispatch = useDispatch();
  const { showModal, setShowModal, documentGroupList } = props;
  const [inputFileText, setInputFileText] = useState<string>(
    'Clique para selecionar ou arraste os documentos aqui',
  );
  const [uploadProgress, setUploadProgress] = useState<UploadProgressTypes>({});
  const [fileList, setFileList] = useState<Array<File>>([]);
  const [filesDescription, setFilesDescription] = useState<FilesDescriptionTypes>({});
  const [documentTypesOptions, setDocumentGroupOptions] = useState<Array<DocumentsTypes>>([]);
  const [documentTypesList, setDocumentTypesList] = useState<Array<any>>([]);
  const [docSelected, setDocSelected] = useState<number>(0);
  const [validareForm, setValidareForm] = useState<boolean>(false);
  const { documents_list } = useSelector<ApplicationState, DocumentsState>(
    (state) => state.session.documents,
  );
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const { request_management_attachments } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const updateUploadProgress = (fileName: string, progress: number) => {
    setUploadProgress({
      ...uploadProgress,
      [`${fileName}`]: progress,
    });
  };

  const handleFile = async (event: any) => {
    await setFileList(Object.values(event.target.files));
  };

  const removeFile = (fileIndex: number) => {
    if (!fileList) return;

    const currentFile = Object.values(fileList)[fileIndex];
    setFileList(
      fileList.filter((file) => file.name !== currentFile.name || file.size !== currentFile.size),
    );
    const descriptionListCopy = filesDescription;
    delete descriptionListCopy[currentFile.name];
    setFilesDescription(descriptionListCopy);
  };

  const submitFiles = async (event: any) => {
    fileList.forEach((file, index) => {
      event.preventDefault();
      const docType = document.querySelector(
        `input[name=document_type_id-${index}]`,
      ) as HTMLInputElement;

      const date = document.querySelector(`input[id=expirationDate-${index}]`) as HTMLInputElement;
      const expirationDate = date && moment(date.value, 'DD/MM/YYYY');
      const expirationDateFormatted = expirationDate.isValid()
        ? expirationDate.format('YYYY-MM-DD')
        : '';
      const filename = file.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[()\s-,()]/gi, '_')
        .replace(/[^\w.-]/g, '_');

      const formData = new FormData();
      formData.append('selectedFile', file);
      formData.append('filename', filename);
      formData.append('document_type_id', docType && docType.value);
      formData.append('expirationDate', expirationDateFormatted);
      formData.append('description', filesDescription[file.name] || '');

      const cnpj = documents_list?.current_cnpj
        ? documents_list?.current_cnpj.match(/\d+/g)?.join('') || ''
        : profile?.cnpj;

      formData.append('cnpj', cnpj);

      setValidareForm(false);
      dispatch(saveDocumentToDatabase(formData, file.name, updateUploadProgress));
    });
  };

  useEffect(() => {
    (async () => {
      setDocumentTypesList(await getDocumentTypesList(docSelected));
    })();
  }, [docSelected]);

  useEffect(() => {
    setDocumentGroupOptions(
      documentTypesList.map((document_type) => {
        return { value: document_type.id, label: document_type.description };
      }),
    );
  }, [documentTypesList]);

  useEffect(() => {
    if (!fileList) return setInputFileText('Clique para selecionar ou arraste os documentos aqui');

    if (Object.values(fileList)?.length === 0)
      return setInputFileText('Clique para selecionar ou arraste os documentos aqui');

    return setInputFileText(`${Object.values(fileList)?.length} arquivos selecionados`);
  }, [fileList]);

  return (
    <Modal show={showModal} size="lg">
      <Container>
        <div className="row">
          <div className="col-md-12">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Adicionar documento</h5>
                <button
                  aria-label="Close"
                  className="close"
                  data-dismiss="modal"
                  type="button"
                  onClick={() => setShowModal(false)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body">
                {validareForm && (
                  <div className="alert alert-danger text-center" role="alert">
                    É necessário selecionar a classificação e tipo do documento
                  </div>
                )}
                <div className="form-group form-group-color m-0 mt-3">
                  <label className="my-0" htmlFor="file-upload label-modal">
                    <div className="input-box">
                      <i className="material-icons">file_upload</i>
                      <span>{inputFileText}</span>
                    </div>
                    <input
                      className="input-file"
                      type="file"
                      multiple
                      id="file-upload"
                      onChange={handleFile}
                    />
                  </label>
                </div>
                <div className="text-center">
                  {fileList.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-link mt-1"
                      onClick={() => setFileList([])}
                    >
                      Limpar arquivos selecionados
                    </button>
                  )}
                </div>
                {fileList &&
                  fileList.map((file, index) => (
                    <div
                      key={`${file.name}`}
                      className={`card card-file border-0 p-2 mt-1 ${
                        request_management_attachments?.create_management_attachements[file.name] &&
                        request_management_attachments?.create_management_attachements[file.name]
                          ?.error
                          ? 'card-file-error'
                          : 'card-file'
                      }
                    ${
                      request_management_attachments?.create_management_attachements[file.name] &&
                      request_management_attachments?.create_management_attachements[file.name]
                        ?.loading
                        ? 'card-file-loading'
                        : 'card-file'
                    }
                    `}
                    >
                      <div className="card-body p-1">
                        <div className="form-group">
                          <h6>
                            <span className="badge badge-secondary p-2">
                              {file.name}
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className={`close ml-2 ${
                                  request_management_attachments?.create_management_attachements[
                                    file.name
                                  ] &&
                                  request_management_attachments?.create_management_attachements[
                                    file.name
                                  ]?.error
                                    ? 'text-danger'
                                    : 'text-secondary'
                                }
                    `}
                              >
                                <span aria-hidden="true">×</span>
                              </button>
                            </span>
                          </h6>
                        </div>
                      </div>
                      <FileDocumentData
                        index={index}
                        setDocSelected={setDocSelected}
                        docSelected={docSelected}
                        validareForm={validareForm}
                        documentGroupList={documentGroupList}
                        documentTypesOptions={documentTypesOptions}
                        filesDescription={filesDescription}
                        file={file}
                        setFilesDescription={setFilesDescription}
                      />
                      <hr />
                    </div>
                  ))}
                {fileList?.length > 0 && (
                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={(event) => submitFiles(event)}
                    >
                      {fileList?.length > 1 ? 'Adicionar documentos' : 'Adicionar documento'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Modal>
  );
};

export default ModalAddDocument;
